import { getSDK, awaitSDKInit } from './sdkClient.js'

/*
 * MIGRATION TO CORESERVICE:
 *
 * This file has been updated to use the new CoreService instead of
 * AuthService, BasedService, and SymstoryService for authentication
 * and user management.
 *
 * Key changes:
 * - initializeUserSession() now accepts loginData (user + tokens) instead of authData
 * - TokenManager automatically handles token persistence and refresh
 * - getUserProjects() is used instead of getUser() for project data
 * - updateUserSession() simplified to use CoreService methods
 * - getStoredAuthState() moved to CoreService with TokenManager integration
 */

// THE THIRD SDK CONSTRUCTION SITE, retired. This used to be
// `new SDK()` on a top-level default import of the sdk package — a
// cross-package specifier, and frank stubs every one of them
// (stubExternalsPlugin, smbls/plugins/frank/toJSON.js), so on a served record
// the read collapses and the construction throws
// `SDK is not a constructor` as an unhandled rejection. sdkClient.js carries
// the cure (workspace 162eb3191): `getSDK()` answers a NULL CLIENT in a host
// where the class never arrived, so consumers degrade instead of rejecting.
//
// Reused, not re-implemented. The singleton is the same client every other
// surface holds, and `awaitSDKInit` waits on the init promise `getSDK` already
// started — a second `initialize()` here would wipe auth state on the real SDK
// (see sdkClient.js's awaitSDKInit header; bootShell.js:1336 does exactly this
// pair before it assigns `context.methods.sdk`).
export async function initializeSDK(ctx) {
  const sdk = getSDK()
  await awaitSDKInit()
  ctx.methods.sdk = sdk
  return sdk
}

export async function updateSDKContext({ userData, project }) {
  const ctx = {}

  if (userData) {
    ctx.user = userData.user
  }
  if (project) {
    ctx.appKey = project.key
    ctx.project = project
  }

  await this.sdk.updateContext({
    ...ctx,
    state: this.getRootState()
  })
}

export async function initializeUserSession(options) {
  const { loginData } = options

  try {
    // Store the current timestamp in sessionStorage
    sessionStorage.setItem('lastInitUserSessionTime', Date.now().toString())

    // Extract user and tokens from login response
    const { user: userData, tokens } = loginData

    // Add projects to userData
    const userInfo = {
      ...userData,
      currentUser: userData,
      userId: userData.id
    }

    if (userData.projectId && userData.projectName && userData.appKey) {
      const contextUpdate = {
        userData: userInfo
      }
      await this.call('updateSDKContext', contextUpdate)

      this.call('updateApplicationState', {
        authToken: tokens.accessToken,
        userData: userInfo
      })
      // SIGNUP-INTENT — the link's preset/plan/source, sent once after an
      // explicit sign-in (functions/signupIntent.js); never breaks it.
      try {
        this.call('sendSignupIntent')
      } catch (_) {}

      return
    }

    this.call('updateApplicationState', {
      authToken: tokens.accessToken,
      userData: userInfo
    })
    // SIGNUP-INTENT — see the branch above
    try {
      this.call('sendSignupIntent')
    } catch (_) {}
  } catch (error) {
    console.error('Failed to initialize user session:', error)
    throw error
  }
}

export async function updateUserSession({ projectId }) {
  try {
    // Get current user data from CoreService
    const currentUser = await this.sdk.getUserProfile()
    const userProjects = await this.sdk.getUserProjects()

    const userData = {
      ...currentUser,
      currentUser,
      userId: currentUser.id,
      projects: userProjects || []
    }

    if (projectId) {
      const basicProject = await this.sdk.getProject(projectId)

      if (basicProject && basicProject.id) {
        const { key: appKey, name: projectName } = basicProject
        const currentProjectMember = basicProject.members.find(
          (m) => m && m.user && m.user.id === userData.id
        )
        const projectRole = currentProjectMember ? currentProjectMember.role : 'guest'

        const contextUpdate = {
          userData,
          ...(basicProject && { project: basicProject }),
          ...(appKey && { appKey }),
          ...(projectRole && { projectRole })
        }
        await this.call('updateSDKContext', contextUpdate)

        const loadedProjectData = await this.call('loadProjectData', projectId)
        const projectData = {
          ...basicProject,
          ...loadedProjectData,
          ...(appKey && { appKey }),
          ...(projectId && { projectId }),
          ...(projectName && { projectName }),
          ...(projectRole && { projectRole })
        }

        return this.call('updateApplicationState', {
          userData,
          projectData
        })
      }
    }

    this.call('updateApplicationState', { userData })
  } catch (error) {
    console.error('[updateUserSession] Error:', error)
    throw error
  }
}
